import { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, DialogModal } from '@/components';
import DOMPurify from 'dompurify';
import { formatTime } from '@/hooks/use-solver-timer';
import { toast } from 'react-toastify';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { useApi } from '@/api/useApi.tsx';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { OK_STATUS_CODE } from '@/constants';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import useVideoCompress from '@/hooks/use-video-compress.ts';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import clsx from 'clsx';
import { useQueryClient } from 'react-query';
import { SolveQuestionRequest, ExpertResolveQuestionSolveRequest, ExpertResolveQuestionSolveRequestSolutionTypeEnum } from '@dopingtech/live_expert_bff_api_ts';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import * as Sentry from '@sentry/react';

const WARNING_TIME_THRESHOLD = 180;

const VIDEO_LOADING_STATES = {
	IDLE: 'idle',
	LOADING: 'loading',
	LOADED: 'loaded',
	ERROR: 'error'
};

interface SolutionVideoModalProps {
	isOpen: boolean;
	onClose: () => void;
	onRetry?: () => void;
	recordedVideoData: {
		recordedData?: {
			url?: string;
			base64?: string;
		};
		blob?: Blob;
		url?: string;
		fromCamera?: boolean;
		filename?: string;
	} | null;
	activeQuestion: {
		questionId?: string;
		curriculumName?: string;
		curriculumElementUnitName?: string;
	};
	solverTimer: {
		isActive: boolean;
		remainingTime: number;
	};
	onSuccess?: () => void;
	onError?: (error: any) => void;
	showRetryButton?: boolean;
	isResolveMode?: boolean;
	resolveQueueId?: number | null;
}

const SolutionVideoModal = ({
	isOpen,
	onClose,
	onRetry,
	recordedVideoData,
	activeQuestion,
	solverTimer,
	onSuccess,
	onError,
	showRetryButton = true,
	isResolveMode = false,
	resolveQueueId = null
}: SolutionVideoModalProps) => {
	const { value: isActiveSolverVP8Codec, loading: isActiveSolverVP8CodecLoading } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_VP8_CODEC, false);

	const { value: isActiveSolverFFMPEG, loading: isActiveSolverFFMPEGLoading } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_FFMPEG, false);

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { SolverQuestionControllerApi, ExpertResolveQuestionControllerApi } = useApi();

	const [showRetryModal, setShowRetryModal] = useState(false);
	const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
	const [videoUrl, setVideoUrl] = useState(null);
	const [disabledButton, setDisabledButton] = useState(false);
	const [uploadedFileName, setUploadedFileName] = useState(null);
	const [videoLoaded, setVideoLoaded] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const [videoLoadingState, setVideoLoadingState] = useState(VIDEO_LOADING_STATES.IDLE);
	const [isUploading, setIsUploading] = useState(false);

	const uploadInProgressRef = useRef(false);
	const currentVideoDataRef = useRef(null);
	const abortControllerRef = useRef(null);

	const { compressVideo, progress } = useVideoCompress();

	const resetAllStates = useCallback(() => {
		setShowRetryModal(false);
		setShowDeleteVideoModal(false);
		setVideoUrl(null);
		setDisabledButton(false);
		setUploadedFileName(null);
		setVideoLoaded(false);
		setVideoError(false);
		setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		setIsUploading(false);
		uploadInProgressRef.current = false;
		currentVideoDataRef.current = null;

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
		}
	}, []);

	const { data: uploadUrl, isLoading: isUploadUrlLoading } = useQueryProvider(
		[QUERY_KEYS.getUploadUrl, activeQuestion?.interactionId, recordedVideoData?.url],
		() => SolverQuestionControllerApi.getUploadUrl(isActiveSolverVP8Codec ? 'video/webm' : 'video/mp4'),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: isOpen && !isActiveSolverVP8CodecLoading && !isActiveSolverFFMPEGLoading
		}
	);

	const solveQuestion = useMutationProvider((body: SolveQuestionRequest) => SolverQuestionControllerApi.solveQuestion(body), {
		onError: error => {
			ExceptionManager.errorHandler(error);
			onError?.(error);
			setDisabledButton(false);
		},
		onSuccess: () => {
			toast.success('Video çözümün başarıyla gönderildi.');
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getRemainingPassedQuestionCount]
			});
			onSuccess?.();
			onClose();
		}
	});

	const solveResolveQuestion = useMutationProvider(
		(body: ExpertResolveQuestionSolveRequest) => ExpertResolveQuestionControllerApi.solveResolveQuestion(resolveQueueId!, body),
		{
			onError: error => {
				ExceptionManager.errorHandler(error);
				onError?.(error);
				setDisabledButton(false);
			},
			onSuccess: () => {
				toast.success('Video çözümün başarıyla gönderildi.');
				onSuccess?.();
				onClose();
			}
		}
	);

	const getVideoDataHash = useCallback(videoData => {
		if (!videoData) return null;

		return JSON.stringify({
			url: videoData.recordedData?.url || videoData.url,
			base64: videoData.recordedData?.base64?.substring(0, 100),
			blobSize: videoData.blob?.size,
			filename: videoData.filename,
			fromCamera: videoData.fromCamera,
			timestamp: Date.now()
		});
	}, []);

	const handleVideoLoadStart = () => {
		if (uploadedFileName && isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.LOADING);
			setVideoLoaded(false);
			setVideoError(false);
		}
	};

	const handleVideoCanPlay = () => {
		if (isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.LOADED);
			setVideoLoaded(true);
			setVideoError(false);
		}
	};

	const handleVideoError = () => {
		if (uploadedFileName && videoLoadingState === VIDEO_LOADING_STATES.LOADING && isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.ERROR);
			setVideoError(true);
			setVideoLoaded(false);
			toast.error('Yüklediğiniz videoda cihaz kaynaklı bir hata oluştu. Yeniden çöz butonunu kullanarak soruyu yeniden çözebilirsiniz.', {
				toastId: 'video-error-toast'
			});
		}
	};

	useEffect(() => {
		if (uploadedFileName && isOpen) {
			setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
			setVideoLoaded(false);
			setVideoError(false);
		}
	}, [uploadedFileName, isOpen]);

	const purify = dirty => ({
		__html: DOMPurify.sanitize(dirty)
	});

	const isSendButtonDisabled = () => {
		return disabledButton || solveQuestion.isLoading || solveResolveQuestion.isLoading || isUploadUrlLoading || !uploadUrl || !videoUrl || !uploadedFileName || !videoLoaded || videoError || isUploading;
	};

	const getButtonText = () => {
		if (isUploading || !uploadedFileName) {
			return 'Video Yükleniyor...';
		}
		if (!videoLoaded) {
			return 'Video Hazırlanıyor...';
		}
		if (solveQuestion.isLoading || solveResolveQuestion.isLoading) {
			return 'Gönderiliyor...';
		}
		if (isUploadUrlLoading) {
			return 'Hazırlanıyor...';
		}
		return 'Videoyu Gönder';
	};

	const onSubmitVideo = async e => {
		e.preventDefault();

		if (!isOpen) return;

		if (!uploadedFileName) {
			toast.error('Video henüz yüklenmedi! Lütfen bekleyin.');
			return;
		}

		if (!videoLoaded || videoError) {
			toast.error('Video oynatılamıyor! Lütfen yeniden deneyin.');
			return;
		}

		if (!activeQuestion?.questionId) {
			toast.error('Soru bilgisi bulunamadı!');
			return;
		}

		try {
			setDisabledButton(true);

			if (isResolveMode && resolveQueueId) {
				await solveResolveQuestion.mutateAsync({
					solutionVideo: uploadedFileName,
					solutionType: ExpertResolveQuestionSolveRequestSolutionTypeEnum.Expert
				});
			} else {
				await solveQuestion.mutateAsync({
					solutionVideo: uploadedFileName,
					questionId: activeQuestion.questionId
				});
			}
		} catch (error) {
			console.error('Video gönderme hatası:', error);
			toast.error('Video gönderilirken hata oluştu!');
			setDisabledButton(false);
		}
	};

	const handleRetryClick = () => {
		setShowRetryModal(true);
	};

	const handleConfirmRetry = () => {
		setShowRetryModal(false);

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}

		setUploadedFileName(null);
		setVideoLoaded(false);
		setVideoError(false);
		setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		setVideoUrl(null);
		setIsUploading(false);
		uploadInProgressRef.current = false;
		currentVideoDataRef.current = null;

		onRetry?.();
	};

	const onCloseSolutionVideoModal = () => {
		setShowDeleteVideoModal(true);
	};

	const handleDeleteVideo = () => {
		setShowDeleteVideoModal(false);
		resetAllStates();
		onClose?.();
		if (location.pathname !== NavigationPath.solverSolve) {
			navigate(NavigationPath.solverSolve);
		}
	};

	useEffect(() => {
		return () => {
			if (videoUrl && videoUrl.startsWith('blob:')) {
				URL.revokeObjectURL(videoUrl);
			}

			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [videoUrl]);

	useEffect(() => {
		if (isOpen) {
			setShowRetryModal(false);
			setShowDeleteVideoModal(false);
			setDisabledButton(false);
			setVideoLoaded(false);
			setVideoError(false);
			setVideoLoadingState(VIDEO_LOADING_STATES.IDLE);
		} else {
			resetAllStates();
		}
	}, [isOpen, resetAllStates]);

	useEffect(() => {
		if (!recordedVideoData || !uploadUrl || uploadedFileName || !isOpen) return;

		const currentVideoHash = getVideoDataHash(recordedVideoData);

		if (currentVideoDataRef.current === currentVideoHash) {
			return;
		}

		if (uploadInProgressRef.current) {
			return;
		}

		const uploadVideo = async () => {
			uploadInProgressRef.current = true;
			currentVideoDataRef.current = currentVideoHash;
			setIsUploading(true);

			abortControllerRef.current = new AbortController();

			try {
				const mimeType = isActiveSolverVP8Codec ? 'video/webm' : 'video/mp4';

				let finalBlob;

				if (recordedVideoData.fromCamera) {
					if (recordedVideoData.recordedData?.url) {
						const videoResponse = await fetch(recordedVideoData.recordedData.url, {
							signal: abortControllerRef.current.signal
						});
						finalBlob = await videoResponse.blob();
					} else {
						throw new Error('Kamera videosu için URL bulunamadı!');
					}
				} else {
					if (recordedVideoData.blob) {
						finalBlob = recordedVideoData.blob;
					} else if (recordedVideoData.url) {
						const videoResponse = await fetch(recordedVideoData.url, {
							signal: abortControllerRef.current.signal
						});
						finalBlob = await videoResponse.blob();
					} else if (recordedVideoData.recordedData?.base64) {
						const base64Url = recordedVideoData.recordedData.base64.startsWith('data:')
							? recordedVideoData.recordedData.base64
							: `data:${mimeType};base64,${recordedVideoData.recordedData.base64}`;

						const base64Data = base64Url.split(',')[1];
						const binaryString = atob(base64Data);
						const bytes = new Uint8Array(binaryString.length);

						for (let i = 0; i < binaryString.length; i++) {
							bytes[i] = binaryString.charCodeAt(i);
						}

						finalBlob = new Blob([bytes], { type: mimeType });
					} else {
						throw new Error("Video blob'u bulunamadı!");
					}
				}

				if (abortControllerRef.current?.signal.aborted) {
					return;
				}

				if (isActiveSolverFFMPEG) {
					try {
						const videoFile = new File([finalBlob], recordedVideoData.filename || 'video.webm', { type: mimeType });

						const result = await compressVideo(videoFile);

						finalBlob = result?.blob;
					} catch (compressionError) {
						Sentry.captureException(new Error(`compressionError | questionId: ${activeQuestion?.questionId} | uploadUrl:  ${uploadUrl.url} | ${compressionError}`));
						console.log('compressionError:', compressionError);
					}
				}

				if (abortControllerRef.current?.signal.aborted) {
					return;
				}

				const uploadResponse = await fetch(uploadUrl.url, {
					method: 'PUT',
					body: finalBlob,
					headers: {
						'Content-Type': mimeType,
						'Content-Length': finalBlob.size.toString()
					},
					signal: abortControllerRef.current.signal
				});

				if (uploadResponse?.status === OK_STATUS_CODE) {
					setUploadedFileName(uploadUrl.fileName);
				} else {
					throw new Error('Video yükleme başarısız!');
				}
			} catch (error) {
				if (error.name === 'AbortError') {
					return;
				}

				console.error('Video yükleme hatası:', error);
				toast.error('Video yüklenirken hata oluştu!');
				setVideoError(true);
			} finally {
				uploadInProgressRef.current = false;
				setIsUploading(false);
				abortControllerRef.current = null;
			}
		};

		uploadVideo();
	}, [recordedVideoData, uploadUrl, uploadedFileName, isOpen, getVideoDataHash]);

	useEffect(() => {
		if (!recordedVideoData || !isOpen) {
			setVideoUrl(null);
			return;
		}

		if (recordedVideoData?.recordedData?.url) {
			setVideoUrl(recordedVideoData.recordedData.url);
			return;
		}

		const mimeType = isActiveSolverVP8Codec ? 'video/webm' : 'video/mp4';

		if (recordedVideoData?.recordedData?.base64) {
			const base64Url = recordedVideoData.recordedData.base64.startsWith('data:') ? recordedVideoData.recordedData.base64 : `data:${mimeType};base64,${recordedVideoData.recordedData.base64}`;

			try {
				const base64Data = base64Url.split(',')[1];
				const binaryString = atob(base64Data);
				const bytes = new Uint8Array(binaryString.length);

				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i);
				}

				const blob = new Blob([bytes], { type: mimeType });
				const blobUrl = URL.createObjectURL(blob);
				setVideoUrl(blobUrl);
			} catch (error) {
				console.log('Error processing base64 video data:', error);
				setVideoUrl(base64Url);
			}
			return;
		}

		if (recordedVideoData?.url) {
			setVideoUrl(recordedVideoData.url);
			return;
		}

		setVideoUrl(null);
	}, [recordedVideoData, isOpen, isActiveSolverVP8Codec]);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onCloseSolutionVideoModal}
				contentClassName="solution-video-modal"
				showCloseButton
				size="lg"
				variant="centered"
				closeOnClickOutside={false}
				customXIcon={<i className="icons-x" />}>
				{!isResolveMode && solverTimer.isActive && (
					<div className="modal-timer">
						<p>
							Kalan Süre:
							<b
								style={{
									color: solverTimer.remainingTime <= WARNING_TIME_THRESHOLD ? 'var(--danger-text)' : 'inherit'
								}}>
								{formatTime(solverTimer.remainingTime)}
							</b>
						</p>
					</div>
				)}

				<div className="modal-header">
					<h2>{recordedVideoData?.fromCamera ? 'Kamera Kaydını Kontrol Et' : 'Çözüm Videosunu Kontrol Et'}</h2>
					<p className="modal-description">Aşağıdaki video çözümünüzü kontrol edin ve gönderin.</p>
				</div>

				<form onSubmit={onSubmitVideo}>
					<div className="modal-body">
						<div className="video-preview">
							{uploadedFileName ? (
								<video
									key={uploadedFileName}
									src={`https://online-solution-media.dopingtech.net/${uploadedFileName}`}
									controls
									autoPlay={true}
									preload="metadata"
									onLoadStart={handleVideoLoadStart}
									onCanPlay={handleVideoCanPlay}
									onError={handleVideoError}
								/>
							) : (
								<div className="video-loader-wrapper">
									<p>Video Yükleniyor... {progress ? `${progress}%` : ''}</p>
								</div>
							)}
						</div>

						<div className="form-section">
							<div className="form-header">
								<div>
									<span className="grade-badge">{activeQuestion?.curriculumName}</span>
									<h3>{activeQuestion?.curriculumElementUnitName}</h3>
								</div>

								{showRetryButton && (
									<button
										type="button"
										className="btn-retry"
										onClick={handleRetryClick}
										disabled={(!isResolveMode && !solverTimer.isActive) || isUploading}>
										<div className="retry-icon" />
										{recordedVideoData?.fromCamera ? 'Tekrar Çek' : 'Yeniden Çöz'}
									</button>
								)}
							</div>
						</div>
					</div>

					<div className="modal-footer">
						<button
							type="submit"
							className={clsx('btn-submit', {
								disabled: isSendButtonDisabled(),
								error: videoError
							})}
							disabled={isSendButtonDisabled()}>
							{getButtonText()}
						</button>
					</div>
				</form>
			</Modal>

			<DialogModal
				isOpen={showRetryModal}
				onClose={() => setShowRetryModal(false)}
				className="solver-dialog-modal-retry"
				variant="warning"
				title="Emin Misin?"
				primaryButtonText={recordedVideoData?.fromCamera ? 'Tekrar Çek' : 'Yeniden Çöz'}
				secondaryButtonText="Vazgeç"
				onSecondaryClick={() => setShowRetryModal(false)}
				onPrimaryClick={handleConfirmRetry}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Videoyu kalan süre içerisinde yeniden çözmek istediğine emin misin?')} />
			</DialogModal>

			<DialogModal
				isOpen={showDeleteVideoModal}
				onClose={() => setShowDeleteVideoModal(false)}
				className="solver-dialog-delete-video-modal"
				variant="trash"
				title="Videoyu Sil"
				primaryButtonText="Evet, Sil"
				secondaryButtonText="Vazgeç"
				onSecondaryClick={() => setShowDeleteVideoModal(false)}
				onPrimaryClick={handleDeleteVideo}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Eğer sayfayı kapatırsan video silinecek. Bu işlem geri alınamaz. Videoyu silmek istediğine emin misin?')} />
			</DialogModal>
		</>
	);
};

export default SolutionVideoModal;
